import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ShoppingBag, MessageCircle, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useOrderList } from "@/context/OrderListContext";
import { useSite } from "@/lib/api";
import { NAV_LINKS, LOGO_URL } from "@/lib/site";

const Logo = ({ big }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img
      src={LOGO_URL}
      alt="Ertan Kasap"
      className={cn(
        "w-auto object-contain transition-all duration-300 ease-out hover:scale-[1.03]",
        big ? "h-[92px] sm:h-[112px] lg:h-[132px]" : "h-12 sm:h-12 lg:h-14"
      )}
    />
  </Link>
);

export const Navbar = () => {
  const { count, setIsOpen } = useOrderList();
  const { data: site } = useSite();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bigLogo = pathname === "/" && !scrolled;

  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand}, ürünleriniz hakkında bilgi almak istiyorum.`)}`
    : "#";

  return (
    <header className={cn("fixed inset-x-0 top-0 z-50 w-full border-b transition-all duration-300", scrolled ? "border-bordo/20 bg-[#140F0C]/85 backdrop-blur-md" : "border-transparent bg-transparent")}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 sm:px-6 lg:px-8">
        {/* Mobil/tablet: hamburger (solda) */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-11 w-11 xl:hidden items-center justify-center rounded-sm border border-charcoal/20 text-charcoal"
          aria-label="Menü"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Logo: masaüstünde solda, mobilde ortada (justify-between) */}
        <Logo big={bigLogo} />

        <nav className="hidden xl:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 font-mono text-[11px] font-medium uppercase tracking-wider transition-colors",
                  isActive ? "text-bordo" : "text-charcoal/60 hover:text-charcoal"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp"
            className="hidden xl:inline-flex items-center gap-2 rounded-sm bg-[#25D366] px-4 py-2 text-sm font-bold uppercase tracking-wide text-charcoal transition-colors hover:bg-[#1eb858]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <button
            data-testid="open-order-list-button"
            onClick={() => setIsOpen(true)}
            className="relative inline-flex h-11 min-w-[44px] items-center justify-center gap-2 rounded-sm border border-bordo/40 px-3 text-sm font-medium uppercase tracking-wide text-bordo transition-colors hover:bg-bordo hover:text-cream"
            aria-label="Sipariş Listem"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden xl:inline">Sipariş Listem</span>
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-bordo px-1 font-mono text-[10px] font-bold text-cream">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-sm bg-paper border-r-4 border-bordo p-0">
          <SheetTitle className="sr-only">Menü</SheetTitle>
          <SheetDescription className="sr-only">Site gezinme menüsü</SheetDescription>
          <div className="flex items-center justify-between border-b border-dashed border-bordo/30 px-5 py-4">
            <Logo />
            <button onClick={() => setMobileOpen(false)} className="rounded-sm p-2 text-charcoal/60 hover:text-bordo">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex flex-col p-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "px-4 py-3 font-mono text-sm uppercase tracking-wider transition-colors",
                    isActive ? "bg-bordo/10 text-bordo" : "text-charcoal/75 hover:bg-bordo/5 hover:text-bordo"
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-sm bg-[#25D366] px-4 py-3 text-sm font-bold uppercase tracking-wide text-charcoal"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Sipariş Ver
            </a>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Navbar;
